import React from "react"
import { useTranslation } from "react-i18next"
import { makeStyles } from "@material-ui/core/styles"
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Typography,
} from "@material-ui/core"
import { navigate, graphql, Link } from "gatsby"
import Typewriter from "typewriter-effect"

import ctoParis from "images/cto-paris.png"
import Competence from "../components/Competence"
import Photo from "../components/Photo"
import Helmet from "react-helmet"

export const query = graphql`
  query HomeQuery {
    photos: allSanityPhotoMoi {
      nodes {
        photoImage {
          asset {
            fixed(height: 500) {
              ...GatsbySanityImageFixed
            }
          }
        }
      }
    }
    competences: allSanityCompetence(sort: { fields: order, order: ASC }) {
      nodes {
        langue
        image {
          asset {
            fluid(maxHeight: 50, maxWidth: 50) {
              ...GatsbySanityImageFluid
            }
          }
        }
        id
        name
      }
    }
    clients: allSanityClient(sort: { fields: order, order: ASC }) {
      nodes {
        image {
          asset {
            fluid {
              ...GatsbySanityImageFluid
            }
          }
        }
        id
        name
      }
    }
    projets: allSanityProjet(
      limit: 6
      sort: { fields: startDate, order: DESC }
    ) {
      nodes {
        langue
        heroImage {
          asset {
            fluid {
              src
            }
          }
        }
        name
        id
        slug {
          current
        }
        brief
        categories {
          title
        }
      }
    }
  }
`

export default function Home({ data }) {
  const classes = useStyles()
  const { t, i18n } = useTranslation()
  const photos = data.photos.nodes
  const competences = data.competences.nodes
  const clients = data.clients.nodes
  const projets = data.projets.nodes

  // console.log("i18n", i18n)

  return (
    <>
      <Helmet title="WS Conseil" />
      <section className={classes.section1}>
        <Container>
          <Grid container direction="row" alignItems="center">
            <Grid item xs={7}>
              <h1 className={classes.big}>
                {t("home.titre1")}
                <Typewriter
                  options={{
                    strings: competences
                      .filter(competence => competence.langue === i18n.language)
                      .map(competence => competence.name),
                    autoStart: true,
                    loop: true,
                    deleteSpeed: 10,
                    pauseFor: 1000,
                    wrapperClassName: classes.typewriter,
                  }}
                  wrapperClassName={classes.big}
                />
              </h1>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate("/projets")}
                style={{ marginRight: "2rem" }}
              >
                {t("home.bouton1")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => navigate("/contact")}
              >
                {t("home.bouton2")}
              </Button>
              <h6 className={classes.subtitle}>(* CTO as a Service)</h6>
              <h6 className={classes.subtitle}>
                {t("home.membre")}
                <img
                  src={ctoParis}
                  height={17}
                  alt="logo CTO.paris"
                  style={{ marginLeft: 20 }}
                />
              </h6>
            </Grid>

            <Grid item xs={5} style={{ padding: 0 }}>
              <Photo photos={photos} />
              {/* <Img fixed={photos[0].photoImage.asset.fixed} alt="me" /> */}
            </Grid>
          </Grid>
        </Container>
      </section>
      <section className={classes.section2}>
        <Container>
          <Grid container direction="row" alignItems="center" spacing={2}>
            {competences.map(competence => {
              if (competence.langue !== i18n.language) return null
              return (
                <Grid item xs={12} sm={6} lg={4} key={competence.id}>
                  <Competence competence={competence} />
                </Grid>
              )
            })}
          </Grid>
        </Container>
      </section>
      <section className={classes.section3}>
        <Container>
          <h3 className={classes.titreSection}>{t("home.titre2")}</h3>
          {clients.map(client => (
            <Client client={client} key={client.id} />
          ))}
        </Container>
      </section>
      <section className={classes.section2}>
        <Container>
          <h3 className={classes.titreSection}>{t("home.titre3")}</h3>
          <Grid container direction="row" spacing={2}>
            {projets.map(projet => {
              if (projet.langue !== i18n.language) return null
              return (
                <Grid item xs={12} sm={6} lg={4} key={projet.id}>
                  <Projet projet={projet} />
                </Grid>
              )
            })}
          </Grid>
        </Container>
      </section>
    </>
  )
}

const Client = ({ client }) => {
  const classes = useStylesSection3()
  return (
    <img
      src={client.image.asset.fluid.src}
      alt={client.name}
      title={client.name}
      className={classes.image}
    />
  )
}

const Projet = ({ projet }) => {
  const classes = useStyleSection4()
  const { t } = useTranslation()

  return (
    <Card className={classes.card}>
      <CardActionArea>
        <Link to={`/projet/${projet.slug.current}`} className={classes.link}>
          <CardMedia
            className={classes.image}
            image={projet.heroImage.asset.fluid.src}
            title={projet.name}
          />
          <CardContent>
            <Typography gutterBottom variant="h5" component="h2">
              {projet.name}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.ellipsis}
            >
              {projet.brief}
            </Typography>
          </CardContent>
        </Link>
      </CardActionArea>
      <CardActions style={{ marginTop: "auto" }}>
        <Button variant="outlined" color="primary">
          <Link to={`/projet/${projet.slug.current}`} className={classes.link}>
            {t("projet.bouton")}
          </Link>
        </Button>
      </CardActions>
    </Card>
  )
}

const useStyles = makeStyles(theme => ({
  section1: {
    flexGrow: 1,
    backgroundColor: theme.palette.bgdark,
    color: "white",
    margin: "0px auto",
  },
  section2: {
    flexGrow: 1,
    backgroundColor: theme.palette.bglight,
    margin: "0px auto",
    padding: "6rem 0",
  },
  section3: {
    flexGrow: 1,
    backgroundColor: theme.palette.bgwhite,
    margin: "0px auto",
    padding: "6rem 0",
  },
  big: {
    lineHeight: "40px",
    fontSize: "36px",
    fontWeight: 500,
  },
  typewriter: {
    color: theme.palette.secondary.main,
  },
  subtitle: {
    lineHeight: "24px",
    fontSize: "16px",
    color: theme.palette.lightgrey,
    fontWeight: 400,
  },
  card: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  titreSection: {
    lineHeight: "30px",
    fontSize: "24px",
    fontWeight: "700",
  },
}))

const useStylesSection3 = makeStyles(theme => ({
  image: {
    padding: "1rem",
    margin: "1rem",
    height: "100px",
    width: "auto",
    filter: `drop-shadow(5px 5px 5px ${theme.palette.primary.light})`,
    transition: "transform 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
}))

const useStyleSection4 = makeStyles(theme => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  image: {
    height: 200,
  },
  link: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:visited": { color: theme.palette.primary.main },
  },
  ellipsis: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
  },
}))
